import { formatArrayToCommaAndString } from '@common/neo.tax';
import { ScreenLoading, SplitView, Stack, Tooltip, useModal, useSplitView } from '@ui-components/neo.tax';
import { ReactComponent as SuccessIcon } from '@ui-components/neo.tax/dist/icons/success-circle.svg';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getFilterableAscProjectColumns, getSortableAscProjectColumns } from '@neo.tax/asc/constants';
import { useAscContext } from '@neo.tax/asc/context';
import {
  useAscPageLockStatus,
  useAscProjectIdOptional,
  useAscProjects,
  useAscProjectsSummary,
  useCreateAscProject,
  useMergeAscProjectsMutation,
  type AscProject,
  useIsCapitalizedProjectClustering,
  useBatchUpdateAscProjectCapitalizableProjectClusteringOverride,
} from '@neo.tax/asc/hooks';
import { mapAscProjectSortField } from '@neo.tax/asc/utils';
import {
  CapitalizableProjectClusteringOverride,
  AscProjectSortFields,
  ColumnVisibilityPopover,
  evictCache,
  FilterPopover,
  FilterSortDataType,
  getPath,
  KeywordSearch,
  PageLayout,
  Path,
  SortDirection,
  SortNulls,
  SortPopover,
  ToastConfigState,
  useColumnVisibility,
  useCurrentCompanyId,
  useFilterConditions,
  useKeywordSearch,
  useNavigateTo,
  useSorting,
  MergeProjectsModal,
  ProjectCreationModal,
  ProjectsTable,
} from '@neo.tax/shared';

import { AscProjectBulkUploadModal } from '../asc-project-bulk-upload-modal';
import { AscProjectDetail } from '../asc-project-detail';
import { BatchUpdateCapitalizableProjectClusteringModal } from '../batch-update-capitalizable-project-clustering-modal';
import { AscProjectsActionsDropdown } from './AscProjectsActionsDropdown';
import { CreateProjectMenuButton } from './CreateProjectMenuButton';
import { useAscProjectsAnalyticIds, useAscProjectsTableColumns } from './hooks';

const PROJECT_DRAWER_WIDTH = 480;

export const AscProjectsLayout = (): React.ReactElement => {
  const { fiscalYearId, periodName } = useAscContext();
  const navigate = useNavigateTo();
  const companyId = useCurrentCompanyId();
  const ascProjectId = useAscProjectIdOptional();
  const { fixedWidth, isOpen, setIsOpen } = useSplitView({
    initialOpen: !!ascProjectId,
    fixedWidth: PROJECT_DRAWER_WIDTH,
  });
  const { isOpen: projectCreationModalOpen, setIsOpen: setProjectCreationModalIsOpen } = useModal();
  const { isOpen: bulkUploadModalOpen, setIsOpen: setBulkUploadModalOpen } = useModal();
  const { isOpen: isMergeProjectsModalOpen, setIsOpen: setIsMergeProjectsModalOpen } = useModal();
  const {
    isOpen: isBatchCapitalizableProjectClusteringOverrideModalOpen,
    setIsOpen: setIsBatchCapitalizableProjectClusteringOverrideModalOpen,
  } = useModal();
  const isCapitalizedProjectClustering = useIsCapitalizedProjectClustering();
  const [
    batchUpdateCapitalizableProjectClusteringOverride,
    { loading: loadingBatchUpdateCapitalizableProjectClusteringOverride },
  ] = useBatchUpdateAscProjectCapitalizableProjectClusteringOverride();
  const {
    cancelMergeProjectsAnalyticId,
    confirmMergeProjectsAnalyticId,
    createAscProjectAnalyticId,
    mergeProjectsAnalyticId,
  } = useAscProjectsAnalyticIds();

  const { loading: loadingPageLockStatus, pageLockStatus } = useAscPageLockStatus({
    disableIfOngoingSync: true,
    disableIfAscCostPendingValidation: false,
    disableIfPeriodLocked: true,
  });

  // Simple column visibility state with proper hook
  const columnVisibilityState = useColumnVisibility({
    columns: [
      { name: 'name', label: 'Name', dataType: FilterSortDataType.TEXT },
      { name: 'productName', label: 'Product', dataType: FilterSortDataType.TEXT },
      { name: 'teams', label: 'Teams', dataType: FilterSortDataType.TEXT },
      { name: 'tickets', label: 'Tickets', dataType: FilterSortDataType.NUMBER },
      { name: 'owner', label: 'Owner', dataType: FilterSortDataType.TEXT },
      { name: 'startDate', label: 'Start Date', dataType: FilterSortDataType.MONTH_YEAR_START },
      { name: 'endDate', label: 'End Date', dataType: FilterSortDataType.MONTH_YEAR_END },
      { name: 'aiGuidance', label: 'AI Guidance', dataType: FilterSortDataType.TEXT },
      { name: 'capitalizationStartDate', label: 'Capitalization Start', dataType: FilterSortDataType.MONTH_YEAR_START },
      { name: 'capitalizationEndDate', label: 'Capitalization End', dataType: FilterSortDataType.MONTH_YEAR_END },
      ...(isCapitalizedProjectClustering
        ? [
            {
              name: 'capitalizableProjectClustering',
              label: 'Next Sync Matching',
              dataType: FilterSortDataType.TEXT as const,
            },
            {
              name: 'matchingTeams',
              label: 'Matching Teams',
              dataType: FilterSortDataType.TEXT as const,
            },
          ]
        : []),
      { name: 'capitalized', label: 'Capitalized', dataType: FilterSortDataType.TEXT },
    ],
    alwaysVisibleColumns: ['name', 'capitalizationStartDate', 'capitalizationEndDate', 'capitalized'],
    initialVisibleColumns: [
      'name',
      'productName',
      'teams',
      'tickets',
      'owner',
      'startDate',
      'endDate',
      'aiGuidance',
      'capitalizationStartDate',
      'capitalizationEndDate',
      ...(isCapitalizedProjectClustering ? ['capitalizableProjectClustering'] : []),
      'capitalized',
    ],
    persistenceKey: 'ascProjects',
  });

  const [propAscProjectId, setPropAscProjectId] = useState(ascProjectId);

  const searchKeywordState = useKeywordSearch();
  const filterableColumns = useMemo(
    () => getFilterableAscProjectColumns(isCapitalizedProjectClustering),
    [isCapitalizedProjectClustering],
  );
  const sortableColumns = useMemo(
    () => getSortableAscProjectColumns(isCapitalizedProjectClustering),
    [isCapitalizedProjectClustering],
  );
  const filterState = useFilterConditions({ columns: filterableColumns, persistenceKey: 'ascProjects' });
  const sortState = useSorting({ columns: sortableColumns, persistenceKey: 'ascProjects' });

  const {
    ascProjects,
    isLoadingMore,
    loading: loadingAscProjects,
    onLoadMoreItems,
  } = useAscProjects({
    searchKeyword: searchKeywordState.searchKeyword,
    filterConditions: filterState.conditions,
    sorting: [
      ...sortState.sorts.map((s) => ({
        field: mapAscProjectSortField(s.column),
        direction: s.direction,
        nulls: SortNulls.NullsLast,
      })),
      // Add ID as secondary sort to ensure deterministic ordering for cursor pagination
      {
        field: AscProjectSortFields.Id,
        direction: SortDirection.Asc,
        nulls: SortNulls.NullsLast,
      },
    ],
  });

  const { ascProjectsSummary, loading: loadingSummary } = useAscProjectsSummary({
    filterConditions: filterState.conditions,
    searchKeyword: searchKeywordState.searchKeyword,
  });

  const [createAscProject, { loading: loadingCreateAscProject }] = useCreateAscProject();

  const [mergeAscProjects, { loading: loadingMergeAscProjects }] = useMergeAscProjectsMutation();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const hasAnyDeterministicSelected = useMemo(() => {
    if (!ascProjects || selectedRows.length === 0) return false;
    const selectedSet = new Set(selectedRows);
    return ascProjects.filter((p) => selectedSet.has(p.id)).some((p) => p.isDeterministic);
  }, [ascProjects, selectedRows]);

  const disableMergeProjectsButton = selectedRows.length < 2 || loadingMergeAscProjects || hasAnyDeterministicSelected;

  const handleMergeProjects = async (): Promise<void> => {
    if (!ascProjects) {
      return;
    }
    const selectedAscProjectIds = new Set(selectedRows);
    const ascProjectsToMerge = ascProjects
      .filter((ascProject) => selectedAscProjectIds.has(ascProject.id))
      .map((ascProject) => `"${ascProject.name}"`);
    const projectNames = formatArrayToCommaAndString(ascProjectsToMerge);
    navigate(getPath({ path: Path.ASC_PROJECTS, companyId, fiscalYearId, periodName }));

    await mergeAscProjects({
      variables: {
        companyId,
        input: {
          ascProjectIds: selectedRows,
        },
        fiscalYearId,
        periodName,
      },
      update: (cache) => {
        evictCache(cache, ['getContributor', 'getAscProjects']);
      },
      onCompleted: (data) => {
        setSelectedRows([]);
        ToastConfigState.pushToast({
          title: 'Successfully Merged!',
          icon: <SuccessIcon />,
          description: `A new project (${data.mergeAscProjects.name}) has been added.`,
        });
        setIsMergeProjectsModalOpen(false);
        navigate(
          getPath({
            path: Path.ASC_PROJECTS,
            companyId,
            fiscalYearId,
            periodName,
            ascProjectId: data.mergeAscProjects.id,
          }),
        );
      },
      context: {
        loadingToast: {
          title: 'Merge started',
          description: `${projectNames} are being merged. Neo.Tax recalculating effort, project name and project description.`,
        },
        errorToast: {
          title: 'Failed to merge projects',
          description: 'An unexpected error happened while merging projects. Please try again.',
        },
      },
    });
  };

  const handleCreateAscProject = async ({
    description,
    name,
  }: {
    name: string;
    description: string;
  }): Promise<void> => {
    const { data } = await createAscProject({
      variables: {
        companyId,
        createAscProjectInput: {
          name,
          description,
        },
        fiscalYearId,
        periodName,
      },
    });

    if (data) {
      const path = getPath({
        path: Path.ASC_PROJECTS,
        companyId,
        fiscalYearId,
        periodName,
        ascProjectId: data.createAscProject.id,
      });
      navigate(path);
    }
  };

  const handleBatchUpdateCapitalizableProjectClusteringOverride = async (
    override: CapitalizableProjectClusteringOverride,
  ): Promise<void> => {
    await batchUpdateCapitalizableProjectClusteringOverride({
      variables: {
        companyId,
        batchUpdateAscProjectCapitalizableProjectClusteringOverrideInput: {
          updates: selectedRows.map((id) => ({ projectId: id, override })),
        },
        fiscalYearId,
        periodName,
      },
      context: {
        errorToast: { title: 'Failed to update matching target status' },
      },
    });
    ToastConfigState.pushToast({
      title:
        override === CapitalizableProjectClusteringOverride.Included
          ? `Included ${selectedRows.length} project${selectedRows.length === 1 ? '' : 's'} as matching targets`
          : `Excluded ${selectedRows.length} project${selectedRows.length === 1 ? '' : 's'} from matching targets`,
    });
    setIsBatchCapitalizableProjectClusteringOverrideModalOpen(false);
    setSelectedRows([]);
  };

  const handleSelectTableRow = (checked: boolean, ascProject: AscProject): void => {
    setSelectedRows((prev) => (checked ? [...prev, ascProject.id] : prev.filter((id) => id !== ascProject.id)));
  };

  const handleSelectAllTableRows = (checked: boolean): void => {
    if (!ascProjects) {
      setSelectedRows([]);
      return;
    }
    setSelectedRows(checked ? ascProjects.map((project) => project.id) : []);
  };

  // Detects changes in projectId
  useEffect(() => {
    if (ascProjectId) {
      setPropAscProjectId(ascProjectId);
    }
    setIsOpen(!!ascProjectId);
  }, [ascProjectId, setIsOpen]);

  const handleTransitionEnd = useCallback(() => {
    setPropAscProjectId(ascProjectId);
  }, [ascProjectId]);

  const columns = useAscProjectsTableColumns({
    clickedRowId: propAscProjectId ?? undefined,
    footerData: ascProjectsSummary,
    onSelectAllRows: handleSelectAllTableRows,
    onSelectRow: handleSelectTableRow,
    rowCount: ascProjects ? ascProjects.length : 0,
    selectedRowIds: selectedRows,
    visibleColumns: columnVisibilityState.visibleColumns,
  });

  // Nothing behind this page turns a write away, so it cannot be handed over before the lock status
  // is known: rendering it now and fading it a moment later leaves a window in which a closed period
  // looks editable and the writes made in it land.
  if (loadingPageLockStatus || !ascProjects) {
    return <ScreenLoading />;
  }

  return (
    <SplitView
      mainView={
        <PageLayout
          title="ASC 350-40 Projects"
          subtitle="Click on a project to review and edit effort."
          trailingContent={
            <Stack direction="row" gap={2}>
              <CreateProjectMenuButton
                analyticId={createAscProjectAnalyticId}
                onCreateSingleProject={(): void => setProjectCreationModalIsOpen(true)}
                onUploadFromCsv={(): void => setBulkUploadModalOpen(true)}
              />
            </Stack>
          }
          locked={pageLockStatus.locked}
          lockedReason={pageLockStatus.lockedReason}
          lockButtons={pageLockStatus.lockButtons}
          fullWidth
        >
          <Stack direction="column" gap={6} fullHeight>
            <Stack gap={5}>
              <KeywordSearch {...searchKeywordState} />
              <FilterPopover itemsDescription="projects" {...filterState} />
              <SortPopover itemsDescription="projects" {...sortState} />
              <ColumnVisibilityPopover {...columnVisibilityState} />
              <Tooltip
                text={
                  hasAnyDeterministicSelected ? 'Projects grouped by ticket relationships cannot be merged' : undefined
                }
              >
                <AscProjectsActionsDropdown
                  canMerge={!disableMergeProjectsButton}
                  mergeAnalyticId={mergeProjectsAnalyticId}
                  mergeDisabledReason={
                    hasAnyDeterministicSelected
                      ? 'Projects grouped by ticket relationships cannot be merged'
                      : selectedRows.length < 2
                        ? 'Select at least 2 projects to merge'
                        : undefined
                  }
                  onMerge={(): void => setIsMergeProjectsModalOpen(true)}
                  showMatchingTargetAction={isCapitalizedProjectClustering}
                  onUpdateMatchingTarget={
                    selectedRows.length > 0
                      ? (): void => setIsBatchCapitalizableProjectClusteringOverrideModalOpen(true)
                      : undefined
                  }
                />
              </Tooltip>
            </Stack>
            <ProjectsTable<AscProject>
              data={ascProjects}
              selectedProjectId={propAscProjectId ?? undefined}
              selectedRowIds={selectedRows}
              columns={columns}
              loadMoreItems={onLoadMoreItems}
              loadingRows={loadingAscProjects}
              loadingFooter={loadingSummary || isLoadingMore}
            />
          </Stack>
          <ProjectCreationModal
            isOpen={projectCreationModalOpen}
            onClose={(): void => setProjectCreationModalIsOpen(false)}
            submit={handleCreateAscProject}
            loading={loadingCreateAscProject}
          />
          <AscProjectBulkUploadModal
            isOpen={bulkUploadModalOpen}
            close={(): void => setBulkUploadModalOpen(false)}
            companyId={companyId}
          />
          <MergeProjectsModal
            isOpen={isMergeProjectsModalOpen}
            loading={loadingMergeAscProjects}
            setIsOpen={setIsMergeProjectsModalOpen}
            submit={handleMergeProjects}
            analyticIds={{
              confirmMergeProjectsId: confirmMergeProjectsAnalyticId,
              cancelMergeProjectsId: cancelMergeProjectsAnalyticId,
            }}
            copy={`Merge combines the selected ${selectedRows.length} project${
              selectedRows.length === 1 ? '' : 's'
            } and updates the title/description. Are you sure you want to merge these projects?`}
          />
          {isCapitalizedProjectClustering && (
            <BatchUpdateCapitalizableProjectClusteringModal
              isOpen={isBatchCapitalizableProjectClusteringOverrideModalOpen}
              count={selectedRows.length}
              loading={loadingBatchUpdateCapitalizableProjectClusteringOverride}
              onClose={(): void => setIsBatchCapitalizableProjectClusteringOverrideModalOpen(false)}
              onSubmit={handleBatchUpdateCapitalizableProjectClusteringOverride}
            />
          )}
        </PageLayout>
      }
      drawerView={propAscProjectId ? <AscProjectDetail ascProjectId={propAscProjectId} /> : null}
      // The project detail drawer is the page's largest write surface — edit, approve, delete,
      // reallocate — and it is SplitView's sibling to `mainView`, so the lock PageLayout applies
      // inside `mainView` cannot reach it. It has to be closed here as well.
      locked={pageLockStatus.locked}
      isOpen={isOpen}
      fixedWidth={fixedWidth}
      onTransitionEnd={handleTransitionEnd}
    />
  );
};
